import axios from "axios";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

function CampaignSummaryCard({ user }) {
    const [campaign, setCampaign] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchCampaign = async () => {
            const userId = user?.id;
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${userId}`,
                );

                // Show campaign if submitted === 1 OR status === 'approved'
                if(
                    res.data &&
                    (res.data.submitted === 1 || res.data.status === "approved")
                ) {
                    setCampaign(res.data);
                } else {
                    setCampaign(null);
                }
            } catch(err) {
                console.error("Failed to load campaign", err);
                setCampaign(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchCampaign();
    }, [user]);

    if(isLoading) {
        return <p className="text-center">Loading campaign info...</p>;
    }
    {
        /* Edit Campaign button below Pitch heading */
    }
    {
        campaign?.status !== "pending" && (
            <div className="flex justify-end mt-8 mb-4">
                <button
                    onClick={() => navigate("/campaign-page")}
                    className="aqi-primary-action rounded bg-navy px-4 py-2 text-white shadow hover:bg-navy-2"
                >
                    Edit Campaign
                </button>
            </div>
        );
    }

    // If no campaign exists, show CTA to create
    if(!campaign) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="text-center space-y-4">
                    <p className="text-lg font-regular text-navy italic mt-6">
                        No campaign found for your account.
                    </p>
                    <button
                        onClick={() => navigate("/campaign-page")}
                        className="px-6 py-2 bg-navy text-white rounded-xl shadow hover:bg-navy-2 font-semibold"
                    >
                        Create Campaign
                    </button>
                </div>
            </div>
        );
    }

    const renderList = (arr) =>
        Array.isArray(arr)
            ? arr.map((item, i) => (
                  <li key={i}>
                      {typeof item === "string" ? item : JSON.stringify(item)}
                  </li>
              ))
            : "—";

    return (
        <div
            className="space-y-6 font-poppins pt-8"
            style={{ paddingLeft: "2.5rem" }}
        >
            {/* Top icon row placeholder (if you have icons, place them here) */}
            <div className="flex items-center gap-4 mb-2">
                {/* ...icon buttons here if any... */}
            </div>
            {/* Edit Campaign button in its own row, just above content */}
            {campaign?.status !== "pending" && (
                <div className="flex justify-end mt-12 mb-4">
                    <button
                        onClick={() => navigate("/campaign-page")}
                        className="px-4 py-2 bg-navy text-white rounded shadow hover:bg-navy-2"
                    >
                        Edit Campaign
                    </button>
                </div>
            )}
            {/* ...existing code... */}
            {campaign?.status === "pending" && (
                <div className="text-[#D4AF37] p-2 mb-4 text-sm italic text-center font-poppins">
                    Your Campaign has been submitted and is under review. You
                    can view it, but edits are disabled.
                </div>
            )}
            {campaign?.status === "rejected" && (
                <div className="text-[#840C28] p-3 mb-4 text-sm font-medium italic text-center font-poppins whitespace-pre-line">
                    <strong>You are Rejected. Please try again.</strong>
                </div>
            )}
            <h2 className="text-xl font-bold text-navy">
                Campaign Overview
            </h2>
            <div>
                <h3 className="text-lg font-bold text-navy mb-2">ABOUT</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <p>
                        <strong className="text-navy">
                            Company Name:
                        </strong>{" "}
                        {campaign.company_name}
                    </p>
                    <p>
                        <strong className="text-navy">
                            Company City:
                        </strong>{" "}
                        {campaign.company_city}
                    </p>
                    <p>
                        <strong className="text-navy">
                            Company State:
                        </strong>{" "}
                        {campaign.company_state}
                    </p>
                    <p>
                        <strong className="text-navy">Investors:</strong>{" "}
                        {campaign.investors_info}
                    </p>
                    <p>
                        <strong className="text-navy">
                            Overview of Pitch:
                        </strong>{" "}
                        {campaign.overview_pitch}
                    </p>
                    <p>
                        <strong className="text-navy">
                            Call to Action:
                        </strong>{" "}
                        {campaign.call_to_action}
                    </p>
                </div>
            </div>

            {/* PITCH Section */}
            <section className="mb-6">
                <h3 className="text-lg md:text-xl font-semibold text-navy border-b border-gray-300 pb-2 mb-4">
                    PITCH
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 mb-4">
                    <div className="flex items-start gap-3">
                        <span className="mt-1 text-navy">
                            <i className="fas fa-exclamation-circle"></i>
                        </span>
                        <div>
                            <div className="text-xs text-navy font-semibold uppercase tracking-wide">
                                Problem
                            </div>
                            <div className="text-base text-navy">
                                {campaign.pitch_problem || "—"}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-start gap-3">
                        <span className="mt-1 text-navy">
                            <i className="fas fa-lightbulb"></i>
                        </span>
                        <div>
                            <div className="text-xs text-navy font-semibold uppercase tracking-wide">
                                Solution
                            </div>
                            <div className="text-base text-navy">
                                {campaign.pitch_solution || "—"}
                            </div>
                        </div>
                    </div>
                </div>
                {Array.isArray(campaign.pitch_slides) &&
                    campaign.pitch_slides.length > 0 && (
                        <div>
                            <h4 className="text-base font-semibold text-navy mb-2">
                                Pitch Slides
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {campaign.pitch_slides.map((slide, idx) => {
                                    const isImage =
                                        /\.(jpg|jpeg|png|gif)$/i.test(
                                            slide.file,
                                        );
                                    const isPDF = /\.pdf$/i.test(slide.file);
                                    return (
                                        <div
                                            key={idx}
                                            className="p-3 border border-gray-200 rounded-md bg-gray-50 shadow-sm flex flex-col items-center"
                                        >
                                            {isImage ? (
                                                <img
                                                    src={slide.file}
                                                    alt={`Slide ${idx + 1}`}
                                                    className="h-60 w-full object-contain mb-2 rounded bg-white"
                                                />
                                            ) : isPDF ? (
                                                <iframe
                                                    src={slide.file}
                                                    title={`PDF Slide ${
                                                        idx + 1
                                                    }`}
                                                    width="100%"
                                                    height="240"
                                                    className="border rounded mb-2 bg-white"
                                                />
                                            ) : (
                                                <div className="text-red-600">
                                                    Unsupported file type
                                                </div>
                                            )}
                                            <p className="text-sm text-gray-700 mt-2 w-full text-center">
                                                {slide.overview || "—"}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
            </section>

            {/* TEAM Section */}
            <div>
                <h3 className="text-lg font-bold text-navy mb-2">TEAM</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Array.isArray(campaign.founders) &&
                        campaign.founders.map((founder, index) => (
                            <div key={index} className="p-3 rounded">
                                <img
                                    src={founder.file}
                                    alt={`Founder ${index + 1}`}
                                    className="h-60 object-contain rounded border bg-white"
                                />
                                <p className="font-semibold mt-2">
                                    {founder.name}
                                </p>
                                <p className="text-sm">{founder.about}</p>
                            </div>
                        ))}
                </div>
            </div>

            {/* UPDATES Section */}
            <div>
                <h3 className="text-lg font-bold text-navy mb-2">
                    UPDATES
                </h3>
                <ul className="list-disc ml-6 text-sm">
                    {renderList(campaign.updates)}
                </ul>
            </div>

            {/* SOCIALS Section */}
            <div>
                <h3 className="text-lg font-bold text-navy mb-2">
                    SOCIAL MEDIA FEED
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <p>
                        <strong className="text-navy">Facebook:</strong>{" "}
                        {campaign.facebook_link}
                    </p>
                    <p>
                        <strong className="text-navy">LinkedIn:</strong>{" "}
                        {campaign.linkedin_link}
                    </p>
                    <p>
                        <strong className="text-navy">Twitter:</strong>{" "}
                        {campaign.twitter_link}
                    </p>
                    <p>
                        <strong className="text-navy">TikTok:</strong>{" "}
                        {campaign.tiktok_link}
                    </p>
                    <p>
                        <strong className="text-navy">YouTube:</strong>{" "}
                        {campaign.youtube_link}
                    </p>
                    <p>
                        <strong className="text-navy">Instagram:</strong>{" "}
                        {campaign.instagram_link}
                    </p>
                </div>
            </div>

            {/* BACKGROUND */}
            {campaign.campaign_background_image && (
                <div className="mt-6">
                    <h3 className="text-lg font-bold text-navy">
                        Campaign Background
                    </h3>
                    <img
                        src={campaign.campaign_background_image}
                        alt="Campaign Background"
                        className="h-72 object-contain rounded mt-2 bg-white"
                    />
                </div>
            )}
        </div>
    );
}

CampaignSummaryCard.propTypes = {
    user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            .isRequired,
    }).isRequired,
};

export default CampaignSummaryCard;
